import React, {useState} from 'react';
import './cookieConsent.css';

const cookieCategories = [
    {
        id: 'necessary',
        name: 'Strictly Necessary Cookies',
        description: 'These cookies are essential for the website to function properly. They enable core functionality such as security, network management, and account access. These cookies cannot be disabled as the website would not function without them.',
        alwaysActive: true,
        cookies: [
            {
                name: 'jwtToken',
                provider: 'Simplicity Investing',
                purpose: 'Stores your authentication token to keep you logged in across browser sessions.',
                duration: 'Persistent',
            },
            {
                name: 'jwtSessionToken',
                provider: 'Simplicity Investing',
                purpose: 'Stores your session-based authentication token. Cleared when you close the browser.',
                duration: 'Session',
            },
        ],
    },
    {
        id: 'functional',
        name: 'Functional Cookies',
        description: 'These cookies allow the website to provide enhanced functionality and personalisation. They may be set by us or by third-party providers whose services we have added to our pages. If you do not allow these cookies then some or all of these services may not function properly.',
        alwaysActive: false,
        cookies: [
            {
                name: 'rc_challenges',
                provider: 'Google reCAPTCHA',
                purpose: 'Tracks completed CAPTCHA challenges for bot detection on the Contact Us form.',
                duration: '6 Months',
            },
            {
                name: '__Host-rc_nonce_*',
                provider: 'Google reCAPTCHA',
                purpose: 'CAPTCHA nonce token used to verify form submissions and prevent spam.',
                duration: 'Session',
            },
        ],
    },
    {
        id: 'analytics',
        name: 'Analytics Cookies',
        description: 'These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our site. They help us to know which pages are the most and least popular and see how visitors move around the site. All information these cookies collect is aggregated and anonymous.',
        alwaysActive: false,
        cookies: [
            {
                name: '_ga',
                provider: 'Google Analytics',
                purpose: 'Registers a unique ID used to generate statistical data on how you use the website.',
                duration: '2 Years',
            },
            {
                name: '_gid',
                provider: 'Google Analytics',
                purpose: 'Registers a unique ID for the current browser session for analytics purposes.',
                duration: '24 Hours',
            },
            {
                name: '_gat',
                provider: 'Google Analytics',
                purpose: 'Used to throttle the request rate when using Google Analytics.',
                duration: '1 Minute',
            },
            {
                name: '_dc_gtm_GTM-KST3QSKP',
                provider: 'Google Tag Manager',
                purpose: 'Used by Google Tag Manager to control loading of analytics scripts.',
                duration: 'Session',
            },
        ],
    },
    {
        id: 'marketing',
        name: 'Marketing Cookies',
        description: 'These cookies may be set through our site by our advertising partners. They may be used by those companies to build a profile of your interests and show you relevant adverts on other sites. They do not store directly personal information, but are based on uniquely identifying your browser and internet device.',
        alwaysActive: false,
        cookies: [
            {
                name: '_gac_*',
                provider: 'Google Ads',
                purpose: 'Contains campaign-related information for Google Ads conversion tracking (if configured in GTM).',
                duration: '90 Days',
            },
        ],
    },
];

function CategoryRow({category, isEnabled, onToggle}) {
    const [expanded, setExpanded] = useState(false);
    const [showCookies, setShowCookies] = useState(false);

    return (
        <div className="cc-category">
            <div
                className="cc-category-header"
                onClick={() => setExpanded(prev => !prev)}
                role="button"
                aria-expanded={expanded}
            >
                <div className="cc-category-left">
                    <span className={`cc-category-arrow ${expanded ? 'expanded' : ''}`}>
                        <i className="fa fa-chevron-right" aria-hidden="true"/>
                    </span>
                    <span className="cc-category-name">{category.name}</span>
                </div>
                <div className="cc-category-right" onClick={e => e.stopPropagation()}>
                    {category.alwaysActive ? (
                        <span className="cc-always-active">Always Active</span>
                    ) : (
                        <label className="cc-toggle" aria-label={`Toggle ${category.name}`}>
                            <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => onToggle(category.id)}
                            />
                            <span className="cc-toggle-slider"/>
                        </label>
                    )}
                </div>
            </div>

            {expanded && (
                <div className="cc-category-body">
                    <p className="cc-category-desc">{category.description}</p>
                    <button
                        className="cc-view-cookies-btn"
                        onClick={() => setShowCookies(prev => !prev)}
                    >
                        {showCookies ? 'Hide Cookies' : 'View Cookies'}
                        <i
                            className={`fa fa-chevron-${showCookies ? 'up' : 'down'} ms-1`}
                            aria-hidden="true"
                        />
                    </button>

                    {showCookies && (
                        <div className="cc-cookies-table-wrap">
                            <table className="cc-cookies-table">
                                <thead>
                                <tr>
                                    <th>Cookie Name</th>
                                    <th>Provider</th>
                                    <th>Purpose</th>
                                    <th>Duration</th>
                                </tr>
                                </thead>
                                <tbody>
                                {category.cookies.map((cookie, idx) => (
                                    <tr key={idx}>
                                        <td><code>{cookie.name}</code></td>
                                        <td>{cookie.provider}</td>
                                        <td>{cookie.purpose}</td>
                                        <td>{cookie.duration}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function CookieSettingsPanelView({preferences, onToggle, onSave, onAcceptAll, onClose}) {
    return (
        <>
            <div className="cc-overlay" onClick={onClose}/>
            <div className="cc-panel" role="dialog" aria-label="Cookie Settings">
                <div className="cc-panel-header">
                    <div className="cc-panel-header-left">
                        {/*<i className="fa fa-cookie-bite cc-panel-header-icon" aria-hidden="true"/>*/}
                        <h5 className="cc-panel-title">Cookie Settings</h5>
                    </div>
                    <button
                        className="cc-panel-close"
                        onClick={onClose}
                        aria-label="Close cookie settings"
                    >
                        <i className="fa fa-times" aria-hidden="true"/>
                    </button>
                </div>

                <div className="cc-panel-body">
                    <p className="cc-panel-intro">
                        When you visit our website, we may store or retrieve information on your browser,
                        mostly in the form of cookies. This information might be about you, your preferences,
                        or your device and is mostly used to make the site work as you expect it to. The
                        information does not usually directly identify you, but it can give you a more
                        personalised web experience. Because we respect your right to privacy, you can choose
                        not to allow some types of cookies. Click on the different category headings to find
                        out more and change our default settings. However, blocking some types of cookies may
                        impact your experience of the site and the services we are able to offer.
                    </p>

                    <div className="cc-categories">
                        {cookieCategories.map(category => (
                            <CategoryRow
                                key={category.id}
                                category={category}
                                isEnabled={preferences[category.id] || false}
                                onToggle={onToggle}
                            />
                        ))}
                    </div>
                </div>

                <div className="cc-panel-footer">
                    <button className="cc-btn cc-btn-accept-all-secondary" onClick={onAcceptAll}>
                        Accept All
                    </button>
                    <button className="cc-btn cc-btn-save-prefs" onClick={onSave}>
                        Save My Preferences
                    </button>
                </div>
            </div>
        </>
    );
}

export default CookieSettingsPanelView;
